'use client'

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { useSession } from 'next-auth/react'
import { organizationsAPI } from '@/lib/api'

interface OrganizationBrandingContextValue {
  logo: string | null
  loading: boolean
  setLogo: (logo: string | null) => void
}

const OrganizationBrandingContext = createContext<OrganizationBrandingContextValue | null>(null)

const FETCH_RETRY_DELAY_MS = 750

export function OrganizationBrandingProvider({ children }: { children: ReactNode }) {
  const { data: session, status } = useSession()
  const isCorporateAdmin = session?.user?.role === 'admin'
  const accessToken = session?.accessToken
  const organizationId = session?.user?.organizationId

  const [logo, setLogo] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const fetchedForOrgId = useRef<string | null>(null)

  useEffect(() => {
    if (status === 'unauthenticated') {
      fetchedForOrgId.current = null
      setLogo(null)
      setLoading(true)
    }
  }, [status])

  useEffect(() => {
    if (status !== 'authenticated' || isCorporateAdmin) {
      setLoading(false)
      return
    }
    if (!organizationId || !accessToken) return

    // Already fetched (or fetching) for this organization — don't refetch on
    // every remount of the pages/components that read this context.
    if (fetchedForOrgId.current === organizationId) return
    fetchedForOrgId.current = organizationId

    setLoading(true)

    const fetchLogo = (isRetry: boolean) => {
      organizationsAPI.get()
        .then(res => {
          setLogo(res.data?.logo ?? null)
          setLoading(false)
        })
        .catch(() => {
          if (!isRetry) {
            setTimeout(() => fetchLogo(true), FETCH_RETRY_DELAY_MS)
            return
          }
          setLogo(null)
          setLoading(false)
        })
    }

    fetchLogo(false)
  }, [status, isCorporateAdmin, accessToken, organizationId])

  return (
    <OrganizationBrandingContext.Provider value={{ logo, loading, setLogo }}>
      {children}
    </OrganizationBrandingContext.Provider>
  )
}

export function useOrganizationBranding() {
  const ctx = useContext(OrganizationBrandingContext)
  if (!ctx) {
    throw new Error('useOrganizationBranding must be used within an OrganizationBrandingProvider')
  }
  return ctx
}
